import React, { useContext, useMemo } from 'react';

import { makeStyles } from '@mui/styles';
import { Grid, Paper, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { PatientTabsContext } from '../../../lib/contexts';
import { AdminProgressBar } from '../../../components/AdminProgressBar';
import useDiet from '../../../lib/hooks/useDiet';
import useMetric from '../../../lib/hooks/useMetric';
import { toCapitalizeFromUnderScore } from '../../../lib/helpers/helpers';

const useStyles = makeStyles((theme) => ({
  patientChartCard: theme.globalClasses.patientChartCard,
  container: {},
  title: {
    fontSize: 10,
  },
  itemBorder: {
    paddingTop: 10,
    paddingBottom: 10,
    borderWidth: '0px 1px 0px 0px',
    borderStyle: 'solid',
    borderColor: theme.palette.admin.background,
  },
  item: {
    textAlign: 'center',
    '& p': {
      fontSize: 20,
      marginBottom: 10,
    },
    border: '1px solid',
    borderColor: theme.palette.primary.main,
    height: '150px',
    width: '150px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    // '&::after': {
    //   content: '""',
    //   position: 'absolute',
    //   top: 0,
    //   left: 0,
    //   right: 0,
    //   bottom: 0,
    //   border: 'inherit',
    //   borderColor: theme.palette.secondary.main,
    //   opacity: 0.5,
    //   pointerEvents: 'none',
    // },
  },
  '@media print': {
    item: {
      '& p': {
        fontSize: 11,
      },
    },
  },
  header: {
    marginBottom: 10,
  },
  text: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem !important',
    },
  },
}));

const PatientGoalsBox = () => {
  const classes = useStyles();
  const {
    state: { currentTab },
  } = useContext(PatientTabsContext);
  let { data } = useMetric('goals', currentTab.id);
  data = data ? data.metric.value : data;
  const activityLevel = useMemo(
    () => (data && data.activityTrend ? toCapitalizeFromUnderScore(data.activityTrend) : 'N/A'),
    [data],
  );
  const customQuestion = useMemo(
    () => (data && data.customField ? toCapitalizeFromUnderScore(data.customField) : 'N/A'),
    [data],
  );
  const { data: diet } = useDiet(
    data && data.idealCode ? data.idealCode : false,
    data && data.idealQuality ? data.idealQuality : false,
    currentTab.id,
  );
  const theme = useTheme();
  const isPhoneView = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Grid
      item
      xs={12}
    >
      <Paper className={classes.patientChartCard}>
        <Typography
          variant={'h2'}
          className={classes.header}
        >
          Goals
        </Typography>
        {!data ? (
          <>
            {data && !data.idealCode ? (
              <Typography>User did not finish Ideal Workflow.</Typography>
            ) : (
              <AdminProgressBar />
            )}
          </>
        ) : (
          <>
            <Grid
              xs={12}
              container
              item
              className={classes.container}
            >
              <Grid
                item
                xs={customQuestion ? 3 : 4}
                className={`${classes.item} ${classes.itemBorder}`}
              >
                <Typography
                  variant={'subtitle2'}
                  className={classes.title}
                >
                  Calories
                </Typography>
                {diet && (
                  <Typography className={classes.text}>
                    {diet.data.adjustedCalories != ''
                      ? Math.floor(diet.data.adjustedCalories).toLocaleString({})
                      : Math.floor(diet.data.calories).toLocaleString({})}
                  </Typography>
                )}
              </Grid>
              <Grid
                item
                xs={customQuestion ? 3 : 4}
                className={`${classes.item} ${classes.itemBorder}`}
              >
                <Typography
                  variant={'subtitle2'}
                  className={classes.title}
                >
                  Diet Style
                </Typography>
                {diet && (
                  <Tooltip title={diet.data.description}>
                    <Typography className={classes.text}>
                      {diet.data.name} / {diet.data.quality}
                    </Typography>
                  </Tooltip>
                )}
              </Grid>
              <Grid
                item
                xs={customQuestion ? 3 : 4}
                className={`${classes.item} ${classes.itemBorder}`}
              >
                <Typography
                  variant={'subtitle2'}
                  className={classes.title}
                >
                  Activity
                </Typography>
                {diet && (
                  <Tooltip title={diet.data.description}>
                    <Typography className={classes.text}>{activityLevel ?? 'N/A'}</Typography>
                  </Tooltip>
                )}
              </Grid>
              {customQuestion && (
                <Grid
                  item
                  xs={3}
                  className={classes.item}
                >
                  <Typography
                    variant={'subtitle2'}
                    className={classes.title}
                    style={{ paddingTop: '4%' }}
                  >
                    Custom Question
                  </Typography>
                  {diet && (
                    <Tooltip title={customQuestion ?? 'N/A'}>
                      <Typography className={classes.text}>{customQuestion ?? 'N/A'}</Typography>
                    </Tooltip>
                  )}
                </Grid>
              )}
            </Grid>
            {data && (
              <>
                <Typography>{data.value}</Typography>
              </>
            )}
          </>
        )}
      </Paper>
    </Grid>
  );
};

export default PatientGoalsBox;
